import { Controller } from "@hotwired/stimulus";

const STATUS_VISIBLE_MS = 4000;
const SMS_KINDS = ["sms_marketing", "sms_transactional"];
const TEXTS_STOPPED_CLASS = "m-comm-prefs__matrix--texts-stopped";

/**
 * Saves the Settings communication preference checkboxes. Turbo Drive is
 * off site-wide, so each change is a fetch PATCH with
 * the CSRF token. Every response carries the resolved state of all four
 * preferences, and the page redraws the boxes from it rather than trusting
 * the click. While a texted STOP has turned off both SMS kinds, the SMS
 * boxes give way to one callout, and the same responses switch them back.
 *
 * Values: url (endpoint), saved / saveError (customer-facing copy rendered
 * by the server so wording lives in the locale file).
 */
export default class extends Controller {
  static targets = [
    "checkbox",
    "matrix",
    "smsCell",
    "textsStopped",
    "textsStoppedMessage",
    "status",
    "error",
    "hint",
    "smsPrompt",
    "smsDisclosure",
  ];

  static values = {
    url: String,
    saved: String,
    saveError: String,
  };

  disconnect() {
    clearTimeout(this.statusTimer);
  }

  /**
   * Saves one checkbox. Disables it while in flight, redraws from the
   * response, and reverts it when the save fails.
   * @param {Event} event change event from a checkbox target
   */
  async toggle(event) {
    const checkbox = event.target;
    const intended = checkbox.checked;
    if (!this.#beginSave()) return;

    this.#reset();
    let succeeded = false;

    try {
      const { ok, body } = await this.#patch({ kind: checkbox.dataset.kind, value: intended });
      if (!ok) return this.#revert(checkbox, intended, this.#firstError(body));

      this.#render(body);
      this.#announce(body.hint);
      succeeded = true;
    } catch {
      this.#revert(checkbox, intended, this.saveErrorValue);
    } finally {
      this.#finishSave(succeeded);
    }
  }

  #beginSave() {
    if (this.inFlight) return false;

    this.inFlight = true;
    this.saveDisabledStates = new Map(this.checkboxTargets.map((checkbox) => [checkbox, checkbox.disabled]));
    this.saveDisabledStates.forEach((_, checkbox) => (checkbox.disabled = true));
    return true;
  }

  #finishSave(succeeded) {
    if (!succeeded) this.saveDisabledStates.forEach((disabled, checkbox) => (checkbox.disabled = disabled));
    this.saveDisabledStates = null;
    this.inFlight = false;
  }

  async #patch(payload) {
    const response = await fetch(this.urlValue, {
      method: "PATCH",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": this.#csrfToken(),
      },
      body: JSON.stringify(payload),
    });
    const body = await response.json().catch(() => ({}));
    return { ok: response.ok, body };
  }

  #csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.content : "";
  }

  #firstError(body) {
    const errors = body && body.errors ? Object.values(body.errors) : [];
    return errors.flat()[0] || this.saveErrorValue;
  }

  #render(body) {
    const preferences = body.preferences || {};
    this.checkboxTargets.forEach((checkbox) => {
      const state = preferences[checkbox.dataset.kind];
      if (!state) return;

      checkbox.checked = state.checked;
      checkbox.disabled = !state.changeable;
    });
    this.#renderSmsNotices(preferences);
    this.#renderTextsStopped(body);
  }

  /**
   * Swaps the two SMS box cells for the stopped-texts callout, or back,
   * the way the server renders the same state.
   * @param {Object} body endpoint response with texts_stopped and texts_stopped_message
   */
  #renderTextsStopped(body) {
    const stopped = Boolean(body.texts_stopped);
    if (stopped && body.texts_stopped_message) {
      this.textsStoppedMessageTarget.textContent = body.texts_stopped_message;
    }
    this.textsStoppedTarget.hidden = !stopped;
    this.smsCellTargets.forEach((cell) => (cell.hidden = stopped));
    this.matrixTarget.classList.toggle(TEXTS_STOPPED_CLASS, stopped);
  }

  /**
   * Shows the add-a-phone prompt while SMS is unavailable, and the text
   * message disclosure while either SMS preference is on.
   * @param {Object} preferences resolved state of each preference, keyed by kind
   */
  #renderSmsNotices(preferences) {
    const sms = preferences.sms_marketing;
    this.smsPromptTarget.hidden = !(sms && sms.unavailable);
    this.smsDisclosureTarget.hidden = !SMS_KINDS.some((kind) => preferences[kind] && preferences[kind].checked);
  }

  #announce(hint) {
    if (hint) return this.#show(this.hintTarget, hint.message);

    this.statusTarget.textContent = this.savedValue;
    this.statusTimer = setTimeout(() => (this.statusTarget.textContent = ""), STATUS_VISIBLE_MS);
  }

  #revert(checkbox, intended, message) {
    checkbox.checked = !intended;
    this.#show(this.errorTarget, message);
  }

  #show(element, message) {
    element.textContent = message;
    element.hidden = false;
  }

  #reset() {
    clearTimeout(this.statusTimer);
    this.statusTarget.textContent = "";
    [this.errorTarget, this.hintTarget].forEach((element) => {
      element.textContent = "";
      element.hidden = true;
    });
  }
}
